import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../services/api';
import { useTheme as useCustomTheme } from '../context/ThemeContext';
import { T, sans } from '../pages/Vendors/VendorDetails';
import { assistantQueries } from '../lib/queries';

/**
 * A small chat widget, docked bottom-right on every page (mounted once in MainLayout). Backed by
 * services/ai/assistant.py on the backend -- a RAG answer (deterministic retrieval + one capped
 * LLM call), not a tool-calling agent, to keep token usage low on a widget meant to stay open all
 * day. Read-only: it cannot start a scan, send a remediation request, or change anything.
 */

interface Message {
  role: 'user' | 'assistant';
  text: string;
  link?: { path: string; label: string } | null;
  pending?: boolean;
  error?: boolean;
}

const WELCOME = "Ask me about a vendor, your portfolio, alerts, remediation, renewals or shared fourth parties.";
const SUGGESTIONS = ['How many vendors do I have?', 'What is my average score?', 'Any contracts renewing soon?'];
export default function FluxAssistant() {
  const navigate = useNavigate();
  const { mode } = useCustomTheme();
  const isDark = mode === 'dark';
  // Cosmetic only -- hides the launcher when a super admin hasn't turned FluxPilot on for this
  // tenant, so the widget isn't shown just to 403 on every message. The backend enforces this
  // independently on every /chat call regardless of what this returns.
  const { data: status } = useQuery(assistantQueries.status());
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const c = {
    ink: isDark ? '#e2e8f0' : T.ink,
    slate: isDark ? '#94a3b8' : T.slate,
    muted: isDark ? '#7c8aa0' : T.muted,
    line: isDark ? '#1f1f1f' : T.bdr,
    surface: isDark ? '#0d0d0d' : T.surface,
    surface2: isDark ? '#141414' : T.surface2,
    bubbleUser: isDark ? '#1d4ed8' : '#2563EB',
    bubbleAssistant: isDark ? '#161616' : '#F8FAFC',
  };

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  // Close on outside click / Escape, same convention as the vendor side panels.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement;
      if (!t.closest('[data-flux-assistant]')) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); window.removeEventListener('keydown', onKey); };
  }, [open]);

  const send = async (text: string) => {
    const question = text.trim();
    if (!question || sending) return;
    const history = messages
      .filter(m => !m.pending && !m.error && m.text)
      .slice(-6)
      .map(m => ({ role: m.role, text: m.text }));
    setMessages(prev => [...prev, { role: 'user', text: question }, { role: 'assistant', text: '', pending: true }]);
    setInput('');
    setSending(true);
    try {
      const { data } = await api.post('/v1/assistant/chat', { question, history });
      setMessages(prev => {
        const next = [...prev];
        next[next.length - 1] = { role: 'assistant', text: data?.answer || "I couldn't find an answer for that.", link: data?.link };
        return next;
      });
    } catch (error: any) {
      const detail = error?.response?.status === 429
        ? "That's a lot of questions at once -- try again in a few minutes."
        : error?.response?.status === 403
        ? (error?.response?.data?.detail || "FluxPilot isn't available right now.")
        : "I couldn't reach the server just now. Try again in a moment.";
      setMessages(prev => {
        const next = [...prev];
        next[next.length - 1] = { role: 'assistant', text: detail, error: true };
        return next;
      });
    } finally {
      setSending(false);
    }
  };

  const onSubmit = (e: React.FormEvent) => { e.preventDefault(); send(input); };

  // Not enabled for this tenant (or still loading) -- render nothing. The backend is the real
  // gate; this just avoids showing a button that would always 403.
  if (!status?.enabled) return null;

  return (
    <div data-flux-assistant style={{ position: 'fixed', right: 20, bottom: 20, zIndex: 1500, fontFamily: sans }}>
      {open && (
        <div ref={panelRef} role="dialog" aria-label="FluxPilot" style={{
          position: 'absolute', right: 0, bottom: 78, width: 420, maxWidth: 'calc(100vw - 40px)',
          // Anchored from the bottom and sized to reach almost up to the fixed 64px app header
          // (a 16px gap under it, same APP_HEADER_HEIGHT convention the vendor/monitoring/
          // remediation side panels use) -- an explicit height, not just maxHeight, so the panel
          // is consistently this tall (message area fills the rest and scrolls) instead of
          // shrinking down to whatever a short conversation's own content happens to need.
          height: 'calc(100vh - 64px - 78px - 20px - 16px)',
          background: c.surface, border: `1px solid ${c.line}`, borderRadius: 12, overflow: 'hidden',
          boxShadow: '0 12px 28px rgba(15,23,42,.18)', display: 'flex', flexDirection: 'column',
          animation: 'fluxAssistantIn 160ms ease-out',
        }}>
          <style>{`@keyframes fluxAssistantIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }`}</style>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 18px', borderBottom: `1px solid ${c.line}` }}>
            <img src="/logo-mark.png" alt="" style={{ width: 28, height: 28, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: c.ink }}>FluxPilot</div>
              <div style={{ fontSize: 12, color: c.muted }}>Answers from your own data</div>
            </div>
            <button aria-label="Close" onClick={() => setOpen(false)} style={{
              background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: c.muted, lineHeight: 1, padding: 2,
            }}>×</button>
          </div>

          <div ref={scrollRef} style={{
            padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 12, background: c.surface2,
            flex: 1, minHeight: 0, overflowY: 'auto',
          }}>
            {messages.length === 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ fontSize: 14, color: c.slate, lineHeight: 1.6 }}>{WELCOME}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {SUGGESTIONS.map(s => (
                    <button key={s} onClick={() => send(s)} style={{
                      textAlign: 'left', fontSize: 13.5, color: isDark ? '#60A5FA' : T.blue, background: c.surface,
                      border: `1px solid ${c.line}`, borderRadius: 8, padding: '9px 12px', cursor: 'pointer', fontFamily: sans,
                    }}>{s}</button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                <div style={{
                  maxWidth: '85%', fontSize: 14, lineHeight: 1.6, padding: '10px 13px', whiteSpace: 'pre-wrap',
                  borderRadius: m.role === 'user' ? '12px 12px 3px 12px' : '12px 12px 12px 3px',
                  background: m.role === 'user' ? c.bubbleUser : c.bubbleAssistant,
                  color: m.role === 'user' ? '#fff' : m.error ? (isDark ? '#F87171' : T.red) : c.ink,
                  border: m.role === 'assistant' ? `1px solid ${c.line}` : 'none',
                }}>
                  {m.pending ? <TypingDots color={c.muted} /> : m.text}
                </div>
                {m.link && !m.pending && (
                  <button onClick={() => { navigate(m.link!.path); setOpen(false); }} style={{
                    marginTop: 6, fontSize: 12, fontWeight: 600, color: isDark ? '#85B7EB' : '#185FA5',
                    background: isDark ? '#0c2a4a' : '#E6F1FB', border: `1px solid ${isDark ? '#1e3a5f' : '#B5D4F4'}`,
                    borderRadius: 99, padding: '4px 12px', cursor: 'pointer', fontFamily: sans,
                  }}>{m.link.label} ↗</button>
                )}
              </div>
            ))}
          </div>

          <form onSubmit={onSubmit} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px', borderTop: `1px solid ${c.line}` }}>
            <input
              value={input} onChange={e => setInput(e.target.value)} placeholder="Ask about your vendors…"
              disabled={sending} maxLength={500}
              style={{
                flex: 1, height: 40, border: `1px solid ${c.line}`, borderRadius: 8, background: c.surface,
                color: c.ink, fontSize: 14, fontFamily: sans, padding: '0 12px', outline: 'none',
              }}
            />
            <button type="submit" disabled={sending || !input.trim()} aria-label="Send" style={{
              width: 40, height: 40, borderRadius: 8, border: 'none', cursor: sending || !input.trim() ? 'default' : 'pointer',
              background: isDark ? '#e2e8f0' : T.ink, color: isDark ? '#0d0d0d' : '#fff',
              opacity: sending || !input.trim() ? 0.5 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15,
            }}>➤</button>
          </form>
        </div>
      )}

      <button onClick={() => setOpen(v => !v)} aria-label={open ? 'Close FluxPilot' : 'Open FluxPilot'} style={{
        width: 64, height: 64, borderRadius: '50%', border: `1px solid ${isDark ? '#2a2a2a' : T.bdr}`, cursor: 'pointer',
        background: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: '0 8px 18px rgba(15,23,42,.28)',
      }}>
        {open
          ? <span style={{ color: T.ink, fontSize: 26, lineHeight: 1 }}>×</span>
          : <img src="/logo-mark.png" alt="" style={{ width: 34, height: 34 }} />}
      </button>
    </div>
  );
}

function TypingDots({ color }: { color: string }) {
  return (
    <span style={{ display: 'inline-flex', gap: 3, alignItems: 'center', height: 14 }}>
      <style>{`
        @keyframes fluxDot { 0%, 80%, 100% { opacity: .25; } 40% { opacity: 1; } }
      `}</style>
      {[0, 1, 2].map(i => (
        <span key={i} style={{
          width: 5, height: 5, borderRadius: 3, background: color,
          animation: `fluxDot 1.1s ${i * 0.15}s infinite ease-in-out`,
        }} />
      ))}
    </span>
  );
}
